import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import HoveredCard from '../../common/card/HoveredCard';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import CartIconBadge from '../../common/cart/CartIconBadge';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { JiraServerContext } from '../jira-server/jira-server.context';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import DesignSpecInfoCard from './cards/DesignSpecInfoCard';
import DesignSpecUserNeedReferenceCard from './cards/DesignSpecUserNeedReferenceCard';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import ProductsSubSectionComponentCard from './cards/ProductsSubSectionComponentCard';
import ProductsSubSectionReleaseCard from './cards/ProductsSubSectionReleaseCard';
import { getProductEntityName } from './Product.config';
import {
  useFindProductDesignSpecById,
  useFindProductDesignSpecVersion,
  useProductEntityPrefixId
} from './Product.hooks';
import {
  BaseProductDesignSpecCardProps,
  ProductDesignSpec
} from './Product.interface';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';

const ProductDesignSpecDetailPage: React.FC = () => {
  const {
    productId: paramProductId = '',
    designSpecId: paramDesignSpecId = ''
  } = useParams<{ productId: string; designSpecId: string }>();
  const productId: number = parseInt(paramProductId) || 0;
  const designSpecId: number = parseInt(paramDesignSpecId) || 0;
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [productDesignSpec, setProductDesignSpec] = useState<
    ProductDesignSpec | undefined
  >();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useContext(JiraServerContext);
  const {
    productDesignSpec: data,
    isLoadingProductDesignSpec,
    findProductDesignSpecError,
    findProductDesignSpec
  } = useFindProductDesignSpecById(productId, designSpecId);
  const {
    productDesignSpecVersion: loadedProductDesignSpecVersion,
    findProductDesignSpecVersion
  } = useFindProductDesignSpecVersion(productId, designSpecId, version);
  const [productDesignSpecVersion, setProductDesignSpecVersion] = useState(
    loadedProductDesignSpecVersion
  );

  const title: string = useProductEntityPrefixId(
    productDesignSpec,
    ProductBoardColumnEntity.DesignSpec
  );
  const { isFieldDisabled } = useTaskEntityFields(productDesignSpec?.task);

  const cardProps: BaseProductDesignSpecCardProps = {
    productId,
    productDesignSpec: productDesignSpec!,
    productDesignSpecVersion,
    onChange: (newProductDesignSpec: Partial<ProductDesignSpec>) => {
      if (productDesignSpec) {
        // notify task components to refresh
        productDesignSpec.task = { ...productDesignSpec.task };
        setProductDesignSpec({
          ...productDesignSpec,
          ...newProductDesignSpec
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!productDesignSpec?.task) {
      return;
    }
    Object.assign(productDesignSpec.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findProductDesignSpec();
  }, [findProductDesignSpec, productId, designSpecId]);
  useEffect(() => {
    if (version) {
      findProductDesignSpecVersion();
    } else {
      setProductDesignSpecVersion(undefined);
    }
  }, [findProductDesignSpecVersion, productId, designSpecId, version]);
  useEffect(
    () =>
      loadedProductDesignSpecVersion &&
      setProductDesignSpecVersion(loadedProductDesignSpecVersion),
    [loadedProductDesignSpecVersion]
  );

  useEffect(() => {
    data && setProductDesignSpec(data);
  }, [data]);

  useEffect(() => {
    setError(findProductDesignSpecError);
  }, [findProductDesignSpecError]);
  const task = productDesignSpecVersion?.task || productDesignSpec?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/${from}`} />
        <HeaderTitle>
          <h2>{isLoadingProductDesignSpec ? 'Loading...' : title}</h2>
          {productDesignSpec && (
            <div className='status-container'>
              <TaskStatusLabel task={productDesignSpec.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          <CartIconBadge
            taskEntity={productDesignSpec}
            onChange={productDesignSpec =>
              setProductDesignSpec(productDesignSpec as ProductDesignSpec)
            }
          />
          {productDesignSpec?.task && (
            <TaskStatusActions
              task={productDesignSpec.task}
              taskEntity={productDesignSpec}
              onChange={(task: Task) => {
                productDesignSpec.task = task;
                setProductDesignSpec({ ...productDesignSpec });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {productDesignSpec && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {productDesignSpecVersion &&
            ` from ${formatDate(productDesignSpecVersion.created)}. `}
          <Link to={`./${productDesignSpec.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {productDesignSpec && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <DesignSpecInfoCard {...cardProps} />
                </Grid>
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={productDesignSpec.task}
                      entity={{
                        title: productDesignSpec.title,
                        description: productDesignSpec.description
                      }}
                      jiraFieldColumns={
                        environment.productConfig?.designSpec?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update productDesignSpec Task jira issues on change
                        productDesignSpec.task.jiraIssues = taskJiraIssues;
                        setProductDesignSpec({ ...productDesignSpec });
                      }}
                    />
                  </Grid>
                )}
                {productDesignSpec.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={productDesignSpec.task}
                      disabled={Boolean(productDesignSpecVersion)}
                      attachments={productDesignSpecVersion?.attachments}
                      onError={cardProps.onError}
                      onChange={handleTaskUpdate}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {productDesignSpec.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusVersionCard
                        showCart
                        taskEntity={productDesignSpec}
                        renderContent={group =>
                          group.version !== productDesignSpec.task.version && (
                            <HoveredCard
                              title={`Version ${group.version}`}
                              content='View the previous version'
                              actions={
                                <Button
                                  variant='text'
                                  color='primary'
                                  component={Link}
                                  startIcon={<Visibility />}
                                  to={`./${productDesignSpec.id}?ver=${group.version}`}
                                >
                                  View
                                </Button>
                              }
                            />
                          )
                        }
                      />
                    </Grid>
                    {task?.children && task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={task}
                          disabled={
                            Boolean(version) ||
                            isFieldDisabled('assignSubTasks')
                          }
                          onError={cardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ProductsSubSectionComponentCard
                        productSubSection={productDesignSpec}
                        productId={productId}
                        readOnlyComponents={
                          productDesignSpecVersion &&
                          (productDesignSpecVersion.components || [])
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ProductsSubSectionReleaseCard
                        productId={productId}
                        productSubSection={productDesignSpec}
                        disabled={Boolean(productDesignSpecVersion)}
                        readOnlyRelease={
                          productDesignSpecVersion &&
                          (productDesignSpecVersion?.release || null)
                        }
                      />
                    </Grid>
                    {task && (
                      <Grid item xs={12}>
                        <TaskAssignCard task={task} />
                      </Grid>
                    )}
                    {productDesignSpec.task && (
                      <>
                        <Grid item xs={12}>
                          <TaskFileListCard
                            task={productDesignSpec.task}
                            disabled={Boolean(productDesignSpecVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TaskDueDateCard
                            task={productDesignSpec.task}
                            disabled={Boolean(productDesignSpecVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                <Grid item xs={12}>
                  <DesignSpecUserNeedReferenceCard {...cardProps} />
                </Grid>
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title={getProductEntityName('designSpec')}
                      apiPath={`products/${productId}/design-specs`}
                      entity={productDesignSpec}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product DesignSpec details updated.
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductDesignSpecDetailPage;
